// Form validation utility functions
import { isIndia } from '../../../utils/countries';

/**
 * Digits of an Indian phone number with the auto-added +91 dial code removed, so that both
 * "9876543210" and "+91 9876543210" (the form's own auto-format) count as 10 local digits.
 */
const indianLocalDigits = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits;
};

import { bankIsIndian } from './bankIsIndian';

export { bankIsIndian };

// GTA (Goods Transport Agency) is a transport-only GST concept. We surface the declaration only
// when the GST registry marks the vendor as a transporter — matched against the nature-of-business
// activities and transport SAC codes (9965/9966/9967).
const TRANSPORT_SIGNAL_RE = /transport|goods\s*transport|\bgta\b|logistic|freight|courier|carriage|\b996[567]\b/i;

/**
 * True when the GST registry data (identity.gst.nature_of_business) indicates a transportation
 * business, so the GTA Registration declaration applies. Returns false when there's no GST data
 * (foreign / KYC-off vendors) — GTA simply doesn't apply then.
 */
export const gstIndicatesTransport = (identity) => {
  const nb = identity?.gst?.nature_of_business;
  if (!Array.isArray(nb)) return false;
  return nb.some((s) => TRANSPORT_SIGNAL_RE.test(String(s)));
};

// Real-time per-field validation
export const validateField = (field, value, formData = {}) => {
  switch (field) {
    case 'companyName':
      if (!value) return 'Company name is required';
      return null;
    case 'countryOrigin':
      if (!value) return 'Country of origin is required';
      return null;
    case 'contactPersonName':
      if (!value) return 'SPOC name is required';
      return null;
    case 'directorName':
      if (!value) return 'Name of the director is required';
      return null;
    case 'directorEmail':
      if (!value) return 'Director email is required';
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) return 'Please enter a valid email address';
      return null;
    case 'designation':
      if (!value) return 'SPOC designation is required';
      return null;
    // The alternate SPOC is mandatory: a second person to reach when the first is unavailable.
    case 'altSpocName':
      if (!value) return 'Alternate SPOC name is required';
      return null;
    case 'altSpocDesignation':
      if (!value) return 'Alternate SPOC designation is required';
      return null;
    case 'altSpocEmail':
      if (!value) return 'Alternate SPOC email is required';
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) return 'Please enter a valid email address';
      return null;
    case 'altSpocPhone':
      if (!value) return 'Alternate SPOC phone number is required';
      if (isIndia(formData.countryOrigin) && indianLocalDigits(value).length !== 10) {
        return 'Phone number must be exactly 10 digits for India';
      }
      return null;
    case 'email':
      if (!value) return 'Email is required';
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) return 'Please enter a valid email address';
      return null;
    case 'phoneNumber': {
      if (!value) return 'Phone number is required';
      if (isIndia(formData.countryOrigin)) {
        if (indianLocalDigits(value).length !== 10) return 'Phone number must be exactly 10 digits for India';
      }
      return null;
    }
    case 'website':
      // Website is optional; accept any format
      return null;
    case 'yearEstablished': {
      if (!value) return null;
      const year = parseInt(value);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < 1800 || year > currentYear) return `Please enter a valid year between 1800 and ${currentYear}`;
      return null;
    }
    case 'registeredAddress':
      if (!value) return 'Registered address is required';
      return null;
    case 'registeredCity':
      if (!value) return 'Registered city is required';
      return null;
    case 'registeredState':
      if (!value) return 'Registered state is required';
      return null;
    case 'registeredCountry':
      if (!value) return 'Registered country is required';
      return null;
    case 'registeredPincode':
      if (!value) return 'Registered pincode is required';
      if (isIndia(formData.countryOrigin) && !/^[1-9][0-9]{5}$/.test(value)) return 'Please enter a valid 6-digit PIN code';
      return null;
    case 'supplyAddress':
      if (!value) return 'Supply address is required';
      return null;
    case 'supplyCity':
      if (!value) return 'Supply city is required';
      return null;
    case 'supplyState':
      if (!value) return 'Supply state is required';
      return null;
    case 'supplyCountry':
      if (!value) return 'Supply country is required';
      return null;
    case 'supplyPincode':
      if (!value) return 'Supply pincode is required';
      if (isIndia(formData.countryOrigin) && !/^[1-9][0-9]{5}$/.test(value)) return 'Please enter a valid 6-digit PIN code';
      return null;
    case 'bankName':
      if (!value) return 'Bank name is required';
      return null;
    case 'accountNumber':
      if (!value) return 'Account number is required';
      return null;
    case 'confirmAccountNumber':
      if (!value) return 'Confirm account number is required';
      if (formData.accountNumber && value !== formData.accountNumber) return 'Account number and confirm account number must match';
      return null;
    case 'accountType':
      if (!value) return 'Account type is required';
      return null;
    case 'bankAddress':
      if (!value) return 'Bank address is required';
      return null;
    case 'ifscCode':
      if (!bankIsIndian(formData)) return null;
      if (!value) return 'IFSC code is required';
      if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) return 'Invalid IFSC code format';
      return null;
    case 'swiftCode':
      if (bankIsIndian(formData)) return null;
      if (!value) return 'Swift code is required for foreign vendors';
      if (!/^[A-Z0-9]{8,11}$/.test(value)) return 'Swift code must be 8-11 characters and contain only letters and numbers';
      return null;
    case 'preferredCurrency':
      if (!value) return 'Preferred currency is required';
      return null;
    case 'msmeStatus':
      if (isIndia(formData.countryOrigin) && !value) return 'MSME status is required';
      return null;
    case 'msmeCategory':
      if (isIndia(formData.countryOrigin) && formData.msmeStatus === 'registered' && !value) return 'MSME category is required';
      return null;
    case 'msmeNumber':
      if (isIndia(formData.countryOrigin) && formData.msmeStatus === 'registered') {
        if (!value) return 'UDYAM registration number is required';
        if (!/^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/.test(value)) return 'UDYAM number must follow format UDYAM-XX-00-XXXXXXX (e.g., UDYAM-MH-00-1234567)';
      }
      return null;
    case 'classificationYear':
      if (isIndia(formData.countryOrigin) && formData.msmeStatus === 'registered') {
        if (!value) return 'Classification year is required';
        const year = parseInt(value);
        const currentYear = new Date().getFullYear();
        if (isNaN(year) || year < 2020 || year > currentYear) {
          return `Please enter a valid classification year between 2020 and ${currentYear}`;
        }
      }
      return null;
    case 'classificationDate':
      if (isIndia(formData.countryOrigin) && formData.msmeStatus === 'registered') {
        if (!value) return 'Classification date is required';
        const date = new Date(value);
        const currentDate = new Date();
        if (isNaN(date.getTime()) || date > currentDate) {
          return 'Please enter a valid classification date (not in the future)';
        }
      }
      return null;
    case 'panNumber':
      if (isIndia(formData.countryOrigin)) {
        if (!value) return 'PAN number is required';
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) return 'Invalid PAN number format';
      }
      return null;
    case 'gstNumber':
      if (isIndia(formData.countryOrigin)) {
        if (!value) return 'GST number is required';
        if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value)) return 'Invalid GST number format';
      }
      return null;
    case 'gtaRegistration':
      if (!value) return 'GTA registration status is required';
      return null;
    default:
      return null;
  }
};

// Step ids (see ../stepPlan.js) → the numbered validation cases below. Numbers are still accepted.
const STEP_ID_TO_CASE = { company: 1, address: 2, bank: 3, msme: 4, categorization: 4, compliance: 5, agreements: 6 };

export const validateStep = (step, formData, keepExistingDocuments = {}, context = {}) => {
  const errors = {};

  if (step === 'identity') {
    if (!formData.countryOrigin) errors.countryOrigin = 'Select your country to continue';
    // Registry verification (GSTIN) is the hard gate for Indian vendors — the server enforces the same.
    // …unless the registry could not help (down, timed out or slow) and the vendor kept the GSTIN as typed (verified before approval).
    if (context.gstinVerificationRequired && !context.gstinVerified && !context.gstinManual) {
      errors.gstinVerified = 'Verify your GSTIN to continue';
    }
    if (context.emailVerificationRequired && !context.emailVerified) {
      errors.emailVerified = 'Verify your email address with the code we send you';
    }
    return errors;
  }

  // The Review step re-runs every earlier step's rules, so nothing can slip through on a
  // resumed session, and adds the final confirmation the vendor ticks before submitting.
  if (step === 'review') {
    const merged = (context.reviewSteps || []).reduce(
      (acc, id) => Object.assign(acc, validateStep(id, formData, keepExistingDocuments, context)),
      {}
    );
    if (!formData.finalConfirmation) {
      merged.finalConfirmation = 'Please confirm the details above are correct before submitting';
    }
    return withoutLockedErrors(merged, context.lockedKeys);
  }

  const caseNo = typeof step === 'number' ? step : STEP_ID_TO_CASE[step];

  switch (caseNo) {
    case 1: // Company Information
      // Business vertical is a fixed value shown read-only, with no input to fix it — so it never blocks
      // the step. A restored session without one saves the default (see getStepData).
      // Email ownership must be proven (OTP) before leaving Step 1 — the server enforces the same gate.
      // In the registration flow this lives on the Identity step instead.
      if (context.emailVerificationRequired && !context.emailVerified && !context.emailOnIdentityStep) {
        errors.emailVerified = 'Verify your email address with the code we send you';
      }
      if (!formData.companyName) errors.companyName = 'Company name is required';
      if (!formData.designation) errors.designation = 'Designation is required';
      if (!formData.countryOrigin) errors.countryOrigin = 'Country of origin is required';

      // Conditional validation based on country
      if (isIndia(formData.countryOrigin)) {
    // Registration number no longer required for India; field removed from UI
      } else {
        if (!formData.incorporationCertificate) {
          errors.incorporationCertificate = 'Company incorporation certificate is required';
        } else {
          const incorpCertError = validateFileField(formData.incorporationCertificate, 'Company incorporation certificate', 10 * 1024 * 1024, ['application/pdf']);
          if (incorpCertError) errors.incorporationCertificate = incorpCertError;
        }
      }

      if (!formData.directorName) errors.directorName = 'Name of the director is required';
      if (!formData.directorEmail) errors.directorEmail = 'Director email is required';
      else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.directorEmail)) {
        errors.directorEmail = 'Please enter a valid email address';
      }

      // Primary SPOC (single point of contact)
      if (!formData.contactPersonName) errors.contactPersonName = 'SPOC name is required';

      if (!formData.email) errors.email = 'Email is required';
      else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }

      // Phone number validation - 10 digits for India only
      if (!formData.phoneNumber) errors.phoneNumber = 'Phone number is required';
      else if (isIndia(formData.countryOrigin)) {
        // For India, validate exactly 10 digits (excluding +91)
        if (indianLocalDigits(formData.phoneNumber).length !== 10) {
          errors.phoneNumber = 'Phone number must be exactly 10 digits for India';
        }
      }

      // Alternate SPOC — mandatory, so there is always a second person to reach.
      if (!formData.altSpocName) errors.altSpocName = 'Alternate SPOC name is required';
      if (!formData.altSpocDesignation) errors.altSpocDesignation = 'Alternate SPOC designation is required';
      if (!formData.altSpocEmail) errors.altSpocEmail = 'Alternate SPOC email is required';
      else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.altSpocEmail)) {
        errors.altSpocEmail = 'Please enter a valid email address';
      }
      if (!formData.altSpocPhone) errors.altSpocPhone = 'Alternate SPOC phone number is required';
      else if (isIndia(formData.countryOrigin) && indianLocalDigits(formData.altSpocPhone).length !== 10) {
        errors.altSpocPhone = 'Phone number must be exactly 10 digits for India';
      }

      // Year of establishment validation (optional field)
      if (formData.yearEstablished) {
        const year = parseInt(formData.yearEstablished);
        const currentYear = new Date().getFullYear();
        if (isNaN(year) || year < 1800 || year > currentYear) {
          errors.yearEstablished = `Please enter a valid year between 1800 and ${currentYear}`;
        }
      }

      // Digital Signature — MANDATORY here (Step 2) so it is uploaded before the Non-MSME
      // declaration (Step 4) and embedded into every agreement document (Step 6).
      if (!keepExistingDocuments.digitalSignature) {
        if (!formData.digitalSignature) {
          errors.digitalSignature = 'Digital signature is required';
        } else {
          const signatureError = validateFileField(formData.digitalSignature, 'Digital signature', 5 * 1024 * 1024, ['image/jpeg', 'image/png']);
          if (signatureError) errors.digitalSignature = signatureError;
        }
        if (formData.digitalSignature instanceof File) {
          const fileName = formData.digitalSignature.name.toLowerCase();
          if (!fileName.endsWith('.jpg') && !fileName.endsWith('.jpeg') && !fileName.endsWith('.png')) {
            errors.digitalSignature = 'Digital signature must be a JPG or PNG image file';
          }
        }
      }
      break;

    case 2: // Address Details
      if (!formData.registeredAddress) errors.registeredAddress = 'Registered address is required';
      if (!formData.registeredCity) errors.registeredCity = 'Registered city is required';
      if (!formData.registeredState) errors.registeredState = 'Registered state is required';
      if (!formData.registeredCountry) errors.registeredCountry = 'Registered country is required';
      if (!formData.registeredPincode) errors.registeredPincode = 'Registered pincode is required';
      else if (isIndia(formData.countryOrigin) && !/^[1-9][0-9]{5}$/.test(formData.registeredPincode)) {
        errors.registeredPincode = 'Please enter a valid 6-digit PIN code';
      }

      if (!formData.supplyAddress) errors.supplyAddress = 'Supply address is required';
      if (!formData.supplyCity) errors.supplyCity = 'Supply city is required';
      if (!formData.supplyState) errors.supplyState = 'Supply state is required';
      if (!formData.supplyCountry) errors.supplyCountry = 'Supply country is required';
      if (!formData.supplyPincode) errors.supplyPincode = 'Supply pincode is required';
      else if (isIndia(formData.countryOrigin) && !/^[1-9][0-9]{5}$/.test(formData.supplyPincode)) {
        errors.supplyPincode = 'Please enter a valid 6-digit PIN code';
      }
      break;

    case 3: // Bank Information
      // Registry-verified bank account (Surepass) is the gate for Indian vendors; the vendor may
      // continue unverified only after acknowledging an upstream outage.
      if (context.bankVerificationRequired && !context.bankVerified && !context.bankUnverifiedAck) {
        errors.bankVerified = 'Verify your bank account to continue';
      }
      if (!formData.bankName) errors.bankName = 'Bank name is required';
      if (!formData.accountNumber) errors.accountNumber = 'Account number is required';
      if (!context.bankVerified) {
        if (!formData.confirmAccountNumber) errors.confirmAccountNumber = 'Confirm account number is required';
        // Validate that account number and confirm account number match
        if (formData.accountNumber && formData.confirmAccountNumber &&
            formData.accountNumber !== formData.confirmAccountNumber) {
          errors.confirmAccountNumber = 'Account number and confirm account number must match';
        }
      }

      if (!formData.accountType) errors.accountType = 'Account type is required';
      if (!formData.bankAddress) errors.bankAddress = 'Bank address is required';
      if (!keepExistingDocuments.bankProof) {
        if (!formData.bankProof) {
          errors.bankProof = 'Bank proof document is required';
        } else {
          const bankProofError = validateFileField(formData.bankProof, 'Bank proof document');
          if (bankProofError) errors.bankProof = bankProofError;
        }
      }

      // IFSC for an Indian account, SWIFT otherwise: decided exactly as BankInformationStep decides which
      // of the two inputs to show, so the required one is always on screen.
      if (bankIsIndian(formData)) {
        if (!formData.branchName) errors.branchName = 'Branch name is required';
        if (!formData.ifscCode) errors.ifscCode = 'IFSC code is required';
        else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) {
          errors.ifscCode = 'Invalid IFSC code format';
        }
      } else {
        // Swift code validation for foreign vendors
        if (!formData.swiftCode) {
          errors.swiftCode = 'Swift code is required for foreign vendors';
        } else if (!/^[A-Z0-9]{8,11}$/.test(formData.swiftCode)) {
          errors.swiftCode = 'Swift code must be 8-11 characters and contain only letters and numbers';
        }
      }
      break;

    case 4: // Supplier Categorization (+ Preferred Currency)
      // Supplier type, supplier group, supplier category, and commodity code removed - collected in approval questionnaire
        if (!formData.preferredCurrency) errors.preferredCurrency = 'Preferred currency is required';
        // MSME-registered Indian vendors must have their Udyam registration looked up (Surepass).
        // …unless the portal could not help and the vendor kept the number as typed (verified before approval).
        if (context.udyamVerificationRequired && formData.msmeStatus === 'registered' && !context.udyamVerified && !context.udyamManual) {
          errors.udyamVerified = 'Look up your Udyam number to continue';
        }

      // Annual turnover validation (optional field)
      if (formData.annualTurnover) {
        const turnover = parseFloat(formData.annualTurnover);
        if (isNaN(turnover) || turnover < 0) {
          errors.annualTurnover = 'Please enter a valid positive number for annual turnover';
        }
      }

      // Employee count is not validated any more: its input went with Additional Classifications, and a
      // rule with no input on screen could never be cleared (a restored "0" would block the step).

      // Annual turnover no longer required
      // Industry sector no longer required (Additional Classifications removed)
      // Products/services removed
      // Employee count no longer required (Additional Classifications removed)

      // Only validate MSME fields for Indian vendors
      if (isIndia(formData.countryOrigin)) {
        if (!formData.msmeStatus) errors.msmeStatus = 'MSME status is required';

        if (formData.msmeStatus === 'registered') {
          if (!formData.msmeCategory) errors.msmeCategory = 'MSME category is required';
          if (!formData.msmeNumber) errors.msmeNumber = 'UDYAM registration number is required';
          else if (!/^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/.test(formData.msmeNumber)) {
            errors.msmeNumber = 'UDYAM number must follow format UDYAM-XX-00-XXXXXXX (e.g., UDYAM-MH-00-1234567)';
          }

          // New MSME classification fields validation - MANDATORY
          if (!formData.classificationYear) {
            errors.classificationYear = 'Classification year is required';
          } else {
            const year = parseInt(formData.classificationYear);
            const currentYear = new Date().getFullYear();
            if (isNaN(year) || year < 2020 || year > currentYear) {
              errors.classificationYear = `Please enter a valid classification year between 2020 and ${currentYear}`;
            }
          }

          if (!formData.classificationDate) {
            errors.classificationDate = 'Classification date is required';
          } else {
            const date = new Date(formData.classificationDate);
            const currentDate = new Date();
            if (isNaN(date.getTime()) || date > currentDate) {
              errors.classificationDate = 'Please enter a valid classification date (not in the future)';
            }
          }

          if (!formData.vendorType) errors.vendorType = 'Vendor type is required';

          if (!keepExistingDocuments.msmeCertificate) {
            if (!formData.msmeCertificate) {
              errors.msmeCertificate = 'MSME certificate is required';
            } else {
              const msmeCertError = validateFileField(formData.msmeCertificate, 'MSME certificate');
              if (msmeCertError) errors.msmeCertificate = msmeCertError;
            }
          }
        } else if (formData.msmeStatus === 'not-registered') {
          if (!keepExistingDocuments.msmeDeclarationFile) {
            if (!formData.msmeDeclaration) {
              errors.msmeDeclaration = 'MSME declaration is required';
            }
          }
        }
      }
      break;

    case 5: // Compliance (India only)
      // Tax registration number removed
      if (isIndia(formData.countryOrigin)) {
        if (!formData.panNumber) errors.panNumber = 'PAN number is required';
        else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber)) {
          errors.panNumber = 'Invalid PAN number format';
        }
        if (!keepExistingDocuments.panDocument) {
          if (!formData.panDocument) {
            errors.panDocument = 'PAN document is required';
          } else {
            const panDocError = validateFileField(formData.panDocument, 'PAN document');
            if (panDocError) errors.panDocument = panDocError;
          }
        }

        if (!formData.gstNumber) errors.gstNumber = 'GST number is required';
        else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber)) {
          errors.gstNumber = 'Invalid GST number format';
        }
        if (!keepExistingDocuments.gstDocument) {
          if (!formData.gstDocument) {
            errors.gstDocument = 'GST document is required';
          } else {
            const gstDocError = validateFileField(formData.gstDocument, 'GST document');
            if (gstDocError) errors.gstDocument = gstDocError;
          }
        }

        // A GST certificate or PAN card carrying a different number than the one verified is the
        // wrong document: Next waits for the right one. Other read warnings (age, a name) do not stop anyone.
        const blockFor = (kind) => (context.documentBlocks?.[kind] || []).find((w) => w.blocks);
        const gstBlock = blockFor('GST_CERTIFICATE');
        if (gstBlock) errors.gstDocument = gstBlock.message;
        const panBlock = blockFor('PAN_CARD');
        if (panBlock) errors.panDocument = panBlock.message;
      } else {
        // For foreign vendors, entire step 5 is skipped; do not enforce these
      }

      // GTA Registration only applies to transport-nature vendors (item 7): required only when the
      // GST registry marks the vendor as a transporter, otherwise the field is hidden entirely.
      if (context.gtaApplicable && !formData.gtaRegistration) errors.gtaRegistration = 'GTA registration status is required';
      break;

    case 6: // Agreements
      // Helper function to check if supplier is ODM
      const isODMSupplier = () => {
        return formData.supplierGroup === 'odm-amber';
      };

      // Helper function to check if supplier is Indian
      const isIndianSupplier = () => {
        return isIndia(formData.countryOrigin);
      };

      // Define which agreements should be shown and validated
      const agreementsToValidate = [
        { id: 'nda', shouldShow: () => !isODMSupplier() },
        { id: 'sqa', shouldShow: () => isIndianSupplier() },
        { id: 'fourM', shouldShow: () => true },
        { id: 'codeOfConduct', shouldShow: () => true },
        { id: 'complianceAgreement', shouldShow: () => isIndianSupplier() },
        { id: 'selfDeclaration', shouldShow: () => true },
        { id: 'overseasAgreement', shouldShow: () => !isIndianSupplier() }
      ];

      // Validate only the agreements that should be shown
      agreementsToValidate.forEach(agreement => {
        if (agreement.shouldShow() && !formData.agreements?.[agreement.id]) {
          errors[`agreements.${agreement.id}`] = 'This agreement must be accepted';
        }
      });

      // item 8: every visible agreement's document must be downloaded before the vendor can move
      // past this step (which keeps the final Submit disabled until all are downloaded).
      const missingDownloads = agreementsToValidate.filter(
        (agreement) => agreement.shouldShow() && !formData.agreementDownloads?.[agreement.id]
      );
      if (missingDownloads.length > 0) {
        errors.agreementDownloads = 'Download every agreement document before continuing';
      }

      // (Digital signature is validated in Step 2 / Company Information now — it must exist before
      // the Non-MSME declaration and agreement documents are generated.)
      break;

    default:
      break;
  }

  return withoutLockedErrors(errors, context.lockedKeys);
};

export const validateFileUpload = (file, maxSize = 10 * 1024 * 1024, allowedTypes = ['image/jpeg', 'image/png', 'application/pdf']) => {
  if (!file) return 'File is required';

  if (file.size > maxSize) {
    return `File size must be less than ${maxSize / (1024 * 1024)}MB`;
  }

  if (!allowedTypes.includes(file.type)) {
    return `File type must be one of: ${allowedTypes.join(', ')}`;
  }

  return null;
};

/**
 * A document the server already holds (restored on resume as { id, file_name, file_size, mime_type }) was
 * checked when it was uploaded. It has no browser `type`/`size`, so checking it again as a File failed
 * forever with "File type must be one of …" while the upload itself showed the file.
 */
const isStagedFile = (value) =>
  Boolean(value) && typeof value === 'object' && !(typeof Blob !== 'undefined' && value instanceof Blob) && value.id != null;

export const validateFileField = (file, fieldName, maxSize = 10 * 1024 * 1024, allowedTypes = ['image/jpeg', 'image/png', 'application/pdf']) => {
  if (!file) return `${fieldName} is required`;
  if (isStagedFile(file)) return null;

  const validationError = validateFileUpload(file, maxSize, allowedTypes);
  if (validationError) {
    return validationError;
  }

  return null;
};

export const canProceedToNextStep = (step, formData, keepExistingDocuments = {}, context = {}) => {
  const errors = validateStep(step, formData, keepExistingDocuments, context);
  return Object.keys(errors).length === 0;
};

/** Registry-locked values are canonical; never second-guess them client-side. */
export const withoutLockedErrors = (errors, lockedKeys) => {
  if (!lockedKeys || !lockedKeys.size) return errors;
  const next = { ...errors };
  lockedKeys.forEach((k) => delete next[k]);
  return next;
};